import i18n from '@risksmart-app/i18n/src/i18n';

import type {
  RelationalEntityGroup,
  WidgetDataSource,
} from '../../gigawidget/types';

type FilterOptions = {
  /** When true, categories marked with isHiddenWhenAggregationsEnabled are excluded */
  isAggregationsEnabled?: boolean;
};

// Singular `*_one` taxonomy key → plural `*_other` key. Used to derive the
// entity group heading in attribute selectors (RSP-5299).
const groupHeadingKey: Record<
  RelationalEntityGroup,
  | 'action_other'
  | 'control_other'
  | 'impact_rating_other'
  | 'indicator_other'
  | 'issue_other'
  | 'obligation_other'
  | 'risk_other'
> = {
  action_one: 'action_other',
  control_one: 'control_other',
  impact_rating_one: 'impact_rating_other',
  indicator_one: 'indicator_other',
  issue_one: 'issue_other',
  obligation_one: 'obligation_other',
  risk_one: 'risk_other',
};

const resolveEntityHeading = (group: RelationalEntityGroup): string =>
  i18n.format(i18n.t(groupHeadingKey[group]), 'titleCase');

export const getStandardFieldOptions = <TDataSource extends WidgetDataSource>(
  categoryGetters: TDataSource['categoryGetters'],
  filterOptions?: FilterOptions
) => {
  const standardFields = categoryGetters
    .filter((cg) => !cg.date)
    .filter(
      (cg) =>
        !filterOptions?.isAggregationsEnabled ||
        !cg.isHiddenWhenAggregationsEnabled
    );

  // RSP-5299: relational data source categories declare an `entityGroup`,
  // which we translate into per-entity headings preserving source order.
  // Single sources have no entityGroup and fall back to one "Standard Fields"
  // group, matching the pre-existing behaviour.
  const hasEntityGroups = standardFields.some((cg) => cg.entityGroup);
  if (hasEntityGroups) {
    const groups: {
      label: string;
      options: { label: string; value: string }[];
    }[] = [];
    for (const cg of standardFields) {
      const entityGroup = cg.entityGroup;
      if (!entityGroup) {
        continue;
      }
      const heading = resolveEntityHeading(entityGroup);
      let group = groups.find((g) => g.label === heading);
      if (!group) {
        group = { label: heading, options: [] };
        groups.push(group);
      }
      group.options.push({ label: cg.name(), value: cg.id });
    }

    return groups;
  }

  return [
    {
      label: 'Standard Fields',
      options:
        standardFields.map((categoryGetter) => ({
          label: categoryGetter.name(),
          value: categoryGetter.id,
        })) ?? [],
    },
  ];
};

export const getDateFieldOptions = <TDataSource extends WidgetDataSource>(
  categoryGetters: TDataSource['categoryGetters'],
  showDateFields?: boolean,
  filterOptions?: FilterOptions
) => {
  return !showDateFields
    ? []
    : [
        {
          label: 'Date Fields',
          options:
            categoryGetters
              .filter((cg) => cg.date)
              .filter(
                (cg) =>
                  !filterOptions?.isAggregationsEnabled ||
                  !cg.isHiddenWhenAggregationsEnabled
              )
              .map((categoryGetter) => ({
                label: categoryGetter.name(),
                value: categoryGetter.id,
              })) ?? [],
        },
      ];
};

export const getCustomAttributeOptions = (
  customAttributeOptions?: { label: string; value: string }[]
) => {
  if (!customAttributeOptions || customAttributeOptions.length < 1) {
    return [];
  }

  return [
    {
      label: 'Custom Fields',
      options: customAttributeOptions,
    },
  ];
};
